import { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import type { Order } from '../types/api';
import { PackageIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import './MyOrders.css';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [sellerNames, setSellerNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    Promise.all([api.getMyOrders(), api.getSellers()])
      .then(([orderData, sellerData]) => {
        setOrders(orderData);
        setSellerNames(Object.fromEntries(sellerData.map(seller => [seller.id, seller.name])));
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  function resolveSellerName(order: Order): string {
    return order.sellerName || (order.sellerId ? sellerNames[order.sellerId] : '') || 'Vendedor nao identificado';
  }

  const formatPrice = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR');

  function getOrderOrigin(order: Order): 'Loja' | 'Online' {
    return order.importedAt ? 'Loja' : 'Online';
  }

  function openOrderDetails(order: Order) {
    setSelectedOrder(order);
  }

  function closeOrderDetails() {
    setSelectedOrder(null);
  }

  async function copyOrderNumber(orderNumber: number) {
    const value = `#${orderNumber}`;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  function printOrder80mm(order: Order) {
    const win = window.open('', '_blank', 'width=420,height=700');
    if (!win) return;

    const itemsHtml = order.items.map(item => `
      <tr>
        <td>${escapeHtml(String(item.quantity))}x</td>
        <td>${escapeHtml(item.productName)}</td>
        <td>${escapeHtml(formatPrice(item.total))}</td>
      </tr>
    `).join('');

    win.document.open();
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Venda #${order.orderNumber}</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            html, body { width: 80mm; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }
            .ticket { width: 72mm; }
            h1 { font-size: 14px; margin: 0 0 4px; }
            .muted { color: #555; font-size: 10px; margin-bottom: 6px; }
            .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
            .order-number { font-size: 15px; font-weight: 800; margin: 4px 0 6px; }
            .divider { border-top: 1px dashed #999; margin: 6px 0; }
            table { width: 100%; border-collapse: collapse; }
            td { vertical-align: top; padding: 2px 0; }
            td:last-child { text-align: right; white-space: nowrap; }
            .totals { font-weight: 700; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <h1>${escapeHtml('MegaX Atacadista')}</h1>
            <div class="muted">Reimpressao 80 mm</div>
            <div class="order-number">Venda #${escapeHtml(String(order.orderNumber))}</div>
            <div class="row"><span>Venda</span><strong>#${escapeHtml(String(order.orderNumber))}</strong></div>
            <div class="row"><span>Cliente</span><span>${escapeHtml(order.customerName)}</span></div>
            <div class="row"><span>Origem</span><span>${escapeHtml(getOrderOrigin(order))}</span></div>
            <div class="row"><span>Status</span><span>${escapeHtml(order.status)}</span></div>
            <div class="row"><span>Data</span><span>${escapeHtml(formatDate(order.createdAt))}</span></div>
            <div class="divider"></div>
            <table><tbody>${itemsHtml}</tbody></table>
            <div class="divider"></div>
            <div class="row totals"><span>Total</span><span>${escapeHtml(formatPrice(order.total))}</span></div>
            ${order.notes ? `<div class="divider"></div><div>Obs: ${escapeHtml(order.notes)}</div>` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
  }

  const filteredOrders = useMemo(() => {
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    const term = searchTerm.trim().toLowerCase();

    return orders.filter(order => {
      const createdAt = new Date(order.createdAt).getTime();
      if (fromTime !== null && createdAt < fromTime) return false;
      if (toTime !== null && createdAt > toTime) return false;
      if (statusFilter && order.status !== statusFilter) return false;
      if (originFilter && getOrderOrigin(order).toLowerCase() !== originFilter) return false;
      if (term) {
        const haystack = [
          String(order.orderNumber),
          order.status,
          getOrderOrigin(order),
          resolveSellerName(order),
          order.notes,
          order.items.map(item => item.productName).join(' '),
        ].join(' ').toLowerCase();

        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [orders, dateFrom, dateTo, searchTerm, statusFilter, originFilter]);

  if (loading) {
    return <div className="myorders-loading">Carregando pedidos...</div>;
  }

  if (error) {
    return <div className="myorders-error">{error}</div>;
  }

  return (
    <section className="myorders section-shell">
      <div className="myorders-header surface-card">
        <div>
          <h2>Meus Pedidos</h2>
          <p>Histórico comercial com rastreio rápido.</p>
        </div>
        <PackageIcon size={22} />
      </div>

      <div className="myorders-filters surface-card">
        <label className="myorders-search">
          Buscar
          <input
            type="search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Venda, item, vendedor, obs..."
          />
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Todos</option>
            <option value="NOVO">NOVO</option>
            <option value="ACEITO">ACEITO</option>
            <option value="SEPARANDO">SEPARANDO</option>
            <option value="PRONTO">PRONTO</option>
            <option value="IMPORTADO">IMPORTADO</option>
            <option value="RECUSADO">RECUSADO</option>
            <option value="SEM_ESTOQUE">SEM_ESTOQUE</option>
            <option value="AGUARDANDO_INTEGRACAO">AGUARDANDO_INTEGRACAO</option>
            <option value="CANCELADO">CANCELADO</option>
          </select>
        </label>
        <label>
          Origem
          <select value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
            <option value="">Todas</option>
            <option value="loja">Loja</option>
            <option value="online">Online</option>
          </select>
        </label>
        <label>
          De
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </label>
        <label>
          Até
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </label>
        <button
          type="button"
          className="myorders-filter-clear"
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('');
            setOriginFilter('');
            setDateFrom('');
            setDateTo('');
          }}
        >
          Limpar
        </button>
      </div>

      {filteredOrders.length === 0 ? (
        <p className="myorders-empty">Nenhum pedido encontrado.</p>
      ) : (
        <div className="myorders-list">
          {filteredOrders.map(order => (
            <button key={order.id} type="button" className="myorders-card" onClick={() => openOrderDetails(order)}>
              <div className="myorders-card-header">
                <span className="myorders-order-number">Venda #{order.orderNumber}</span>
                <span className="myorders-origin">{getOrderOrigin(order)}</span>
                <span className={`myorders-status myorders-status--${order.status}`}>
                  {order.status}
                </span>
                <span className="myorders-date">{formatDate(order.createdAt)}</span>
              </div>
              <p className="myorders-total">{formatPrice(order.total)}</p>
              <p className="myorders-seller">Vendedor: {resolveSellerName(order)}</p>
              <p className="myorders-items-count">{order.items.length} item{order.items.length === 1 ? '' : 's'}</p>
              <div className="myorders-items">
                {order.items.map((item, i) => (
                  <div key={i} className="myorders-item">
                    <span>{item.productName}</span>
                    <span>{item.quantity}x {formatPrice(item.unitPrice)}</span>
                  </div>
                ))}
              </div>
              {order.notes && <p className="myorders-notes">Obs: {order.notes}</p>}
            </button>
          ))}
        </div>
      )}

      {selectedOrder && (
        <div className="myorders-modal" role="dialog" aria-modal="true" aria-label={`Venda ${selectedOrder.orderNumber}`}>
          <button type="button" className="myorders-modal-backdrop" onClick={closeOrderDetails} aria-label="Fechar detalhe" />
          <section className="myorders-modal-card">
            <div className="myorders-modal-header">
                <div>
                <h3>Venda #{selectedOrder.orderNumber}</h3>
                <div className="myorders-modal-order-number">#{selectedOrder.orderNumber}</div>
                <p>{selectedOrder.customerName}</p>
              </div>
              <div className="myorders-modal-header-actions">
                <button type="button" className="myorders-modal-copy" onClick={() => void copyOrderNumber(selectedOrder.orderNumber)}>Copiar venda</button>
                <button type="button" className="myorders-modal-close" onClick={closeOrderDetails}>Fechar</button>
              </div>
            </div>

            <div className="myorders-modal-meta">
              <span><strong>Status:</strong> {selectedOrder.status}</span>
              <span><strong>Data:</strong> {formatDate(selectedOrder.createdAt)}</span>
              <span><strong>Vendedor:</strong> {resolveSellerName(selectedOrder)}</span>
              <span><strong>Origem:</strong> {getOrderOrigin(selectedOrder)}</span>
              <span><strong>CNPJ:</strong> {selectedOrder.customerCnpj || user?.cnpj || '-'}</span>
            </div>

            <div className="myorders-modal-items">
              {selectedOrder.items.map(item => (
                <div key={item.productId} className="myorders-modal-item">
                  <div>
                    <strong>{item.productName}</strong>
                    <span>{item.quantity}x {formatPrice(item.unitPrice)}</span>
                  </div>
                  <strong>{formatPrice(item.total)}</strong>
                </div>
              ))}
            </div>

            <div className="myorders-modal-footer">
              <strong>Total: {formatPrice(selectedOrder.total)}</strong>
              <div className="myorders-modal-actions">
                <button type="button" onClick={() => printOrder80mm(selectedOrder)}>Reimprimir 80 mm</button>
                <button type="button" className="myorders-modal-close" onClick={closeOrderDetails}>Fechar</button>
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
